import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ListResult, PatientListItem } from '../../types/analytics.types';
import { formatShortDate } from '../../lib/formatters';
import { cn } from '@/lib/utils';

interface TableChartDisplayProps {
  data: ListResult;
}

function getDeadlineColor(days: number): string {
  if (days <= 2) return 'text-destructive bg-destructive/10';
  if (days <= 4) return 'text-warning bg-warning/10';
  return 'text-success bg-success/10';
}

function getRiskColor(score: number): string {
  if (score >= 3.5) return 'text-destructive bg-destructive/10';
  if (score >= 2.5) return 'text-warning bg-warning/10';
  return 'text-success bg-success/10';
}

function isTCMPatient(patient: PatientListItem): boolean {
  return 'dischargeDate' in patient && 'daysUntilTCMDeadline' in patient;
}

function isHighRiskPatient(patient: PatientListItem): boolean {
  return 'daysSinceVisit' in patient && 'openGapCount' in patient;
}

function TCMTable({ patients }: { patients: PatientListItem[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="h-9 px-2 text-xs">Patient</TableHead>
          <TableHead className="h-9 px-2 text-xs">Discharged</TableHead>
          <TableHead className="h-9 px-2 text-xs">Reason</TableHead>
          <TableHead className="h-9 px-2 text-xs text-right">Deadline</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {patients.map((patient) => {
          const days = (patient as any).daysUntilTCMDeadline as number;
          return (
            <TableRow key={patient.id}>
              <TableCell className="py-2 px-2 text-xs font-medium">
                {patient.firstName} {patient.lastName}
              </TableCell>
              <TableCell className="py-2 px-2 text-xs text-muted-foreground">
                {formatShortDate((patient as any).dischargeDate)}
              </TableCell>
              <TableCell className="py-2 px-2 text-xs text-muted-foreground truncate max-w-[120px]">
                {(patient as any).dischargeReason}
              </TableCell>
              <TableCell className="py-2 px-2 text-right">
                <span
                  className={cn(
                    'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium',
                    getDeadlineColor(days)
                  )}
                >
                  {days}d
                </span>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function HighRiskTable({ patients }: { patients: PatientListItem[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="h-9 px-2 text-xs">Patient</TableHead>
          <TableHead className="h-9 px-2 text-xs">Risk</TableHead>
          <TableHead className="h-9 px-2 text-xs">Last Visit</TableHead>
          <TableHead className="h-9 px-2 text-xs text-right">Gaps</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {patients.map((patient) => {
          const riskScore = patient.riskScore ?? 0;
          return (
            <TableRow key={patient.id}>
              <TableCell className="py-2 px-2 text-xs font-medium">
                {patient.firstName} {patient.lastName}
              </TableCell>
              <TableCell className="py-2 px-2">
                <span
                  className={cn(
                    'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium',
                    getRiskColor(riskScore)
                  )}
                >
                  {riskScore.toFixed(1)}
                </span>
              </TableCell>
              <TableCell className="py-2 px-2 text-xs text-muted-foreground">
                {formatShortDate((patient as any).lastVisitDate)}
              </TableCell>
              <TableCell className="py-2 px-2 text-xs text-right text-muted-foreground">
                {(patient as any).openGapCount}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function GenericTable({ patients }: { patients: PatientListItem[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="h-9 px-2 text-xs">Patient</TableHead>
          <TableHead className="h-9 px-2 text-xs">Age</TableHead>
          <TableHead className="h-9 px-2 text-xs">PCP</TableHead>
          <TableHead className="h-9 px-2 text-xs">Payer</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {patients.map((patient) => (
          <TableRow key={patient.id}>
            <TableCell className="py-2 px-2 text-xs font-medium">
              {patient.firstName} {patient.lastName}
            </TableCell>
            <TableCell className="py-2 px-2 text-xs text-muted-foreground">
              {patient.age}
            </TableCell>
            <TableCell className="py-2 px-2 text-xs text-muted-foreground truncate max-w-[100px]">
              {patient.pcp ?? '-'}
            </TableCell>
            <TableCell className="py-2 px-2 text-xs text-muted-foreground">
              {patient.payer ?? '-'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function TableChartDisplay({ data }: TableChartDisplayProps) {
  const { patients, totalCount } = data;
  const displayPatients = patients.slice(0, 8);

  const tableType =
    displayPatients.length > 0 && isTCMPatient(displayPatients[0])
      ? 'tcm'
      : displayPatients.length > 0 && isHighRiskPatient(displayPatients[0])
        ? 'high-risk'
        : 'generic';

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-auto">
        {tableType === 'tcm' && <TCMTable patients={displayPatients} />}
        {tableType === 'high-risk' && <HighRiskTable patients={displayPatients} />}
        {tableType === 'generic' && <GenericTable patients={displayPatients} />}
      </div>
      {totalCount > 8 && (
        <div className="text-xs text-muted-foreground text-center py-2 border-t">
          Showing 8 of {totalCount} patients
        </div>
      )}
    </div>
  );
}
